<template>
  <article class="hotspot-card">
    <header class="card-header">
      <h3 class="card-title">{{ title || 'Hot Spots' }}</h3>
    </header>

    <div class="card-body">
      <!-- Loading shimmer -->
      <div v-if="loading" class="hotspot-list duallo-scroll">
        <div v-for="n in 3" :key="n" class="hotspot-item">
          <div class="hotspot-thumb is-loading"></div>
          <div class="hotspot-content">
            <span class="sk sk-line" style="width: 70%"></span>
            <span class="sk sk-line" style="width: 45%; height: 0.7rem"></span>
          </div>
        </div>
      </div>

      <div v-else-if="hotSpots && hotSpots.length > 0" class="hotspot-list duallo-scroll">
        <div v-for="hotspot in hotSpots" :key="hotspot.id" class="hotspot-item">
          <div class="hotspot-thumb" :class="{ 'is-loading': hotspot.thumbnailPath && !imgLoaded[hotspot.id] }">
            <img v-if="hotspot.thumbnailPath" v-show="imgLoaded[hotspot.id]" :src="hotspot.thumbnailPath" class="thumb-image"
              alt="Hot spot thumbnail" @load="imgLoaded[hotspot.id] = true" @error="onThumbError(hotspot, $event)" />
            <img v-else class="thumb-image" :src="noImageAvailable" alt="No image available" />
          </div>

          <div class="hotspot-content">
            <div class="hotspot-label">{{ hotspot.label || 'Hot spot' }}</div>
            <div class="hotspot-meta">{{ formatDate(hotspot.lastUpdatedOn) }}</div>
          </div>

          <div class="hotspot-count">
            <span class="count-num">{{ formatNumber(hotspot.numberOfMarkers || 0) }}</span>
            <span class="count-lbl">{{ (hotspot.numberOfMarkers || 0) === 1 ? 'anomaly' : 'anomalies' }}</span>
          </div>
        </div>
      </div>

      <div v-else class="hotspot-empty">
        <v-icon size="36" color="#4a5a60">mdi-fire-off</v-icon>
        <span>No active hot spots</span>
      </div>
    </div>
  </article>
</template>

<script>
import { useDefaultImage } from '@/utils/ImageUtils'
import { format } from 'timeago.js'
import organizationSasService from '@/services/OrganizationSasService';
import noImageAvailable from '@/assets/no-image-available.svg';

export default {
  name: 'HotSpotSummaryCard',
  props: {
    title: { type: String, default: "" },
    dataFetcher: { type: Function, required: true }
  },
  data() {
    return {
      noImageAvailable,
      organizationId: null,
      hotSpots: [],
      loading: true,
      imgLoaded: {}
    }
  },
  async mounted() {
    this.loading = true
    try {
      const data = await this.dataFetcher()
      this.organizationId = data?.organizationId ?? null
      this.hotSpots = Array.isArray(data?.hotSpots) ? await this.updateHotSpotImagePaths(data.hotSpots) : []
    } catch (err) {
      console.error('Error fetching hotspots', err)
      this.hotSpots = []
    } finally {
      this.loading = false
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return 'unknown date'
      return format(dateStr)
    },
    formatNumber(n) {
      if (n == null) return '0'
      return Number(n).toLocaleString()
    },
    useDefaultImage,
    // On thumbnail error: swap to the default image, but stop shimmering.
    onThumbError(hotspot, e) {
      useDefaultImage(e)
      this.imgLoaded[hotspot.id] = true
    },
    async updateHotSpotImagePaths(hotSpots) {
      if (!Array.isArray(hotSpots) || hotSpots.length === 0) return []

      return Promise.all(
        hotSpots.map(async (hotSpot) => {
          if (!hotSpot.thumbnailPath) return hotSpot
          try {
            const sasUrl = await organizationSasService.getSignedUrl(hotSpot.thumbnailPath);
            return { ...hotSpot, thumbnailPath: sasUrl }
          } catch (error) {
            console.error(`Failed to update thumbnail for hotspot ${hotSpot.id}`, error)
            return { ...hotSpot, thumbnailPath: null }
          }
        })
      )
    }
  }
}
</script>

<style scoped>
.hotspot-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  padding: 16px 18px 8px;
  flex: 0 0 auto;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.card-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 4px 14px 14px;
  display: flex;
  flex-direction: column;
}

.hotspot-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
  padding-right: 6px;
}

.hotspot-item {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.hotspot-item:hover {
  background: rgba(100, 255, 218, 0.05);
  border-color: rgba(100, 255, 218, 0.15);
}

.hotspot-thumb {
  flex: 0 0 52px;
  width: 52px;
  align-self: stretch;      /* fill the item's full height so no background gap shows below the image */
  min-height: 52px;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Shimmer: skeleton rows + per-image loading. */
.sk {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}
.sk-line { display: block; height: 0.9rem; }
.sk::after,
.hotspot-thumb.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
.hotspot-thumb.is-loading { position: relative; }
@keyframes sk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) {
  .sk::after,
  .hotspot-thumb.is-loading::after { animation: none; }
}

.hotspot-content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 10px 10px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
}

.hotspot-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.3;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hotspot-meta {
  font-size: 0.78rem;
  color: #8aa0aa;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Anomaly count — the key metric, right-aligned. */
.hotspot-count {
  flex: 0 0 auto;
  align-self: center;
  padding: 0 14px 0 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}
.count-num {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--duallo-accent-muted);
  font-variant-numeric: tabular-nums;
}
.count-lbl {
  margin-top: 3px;
  font-size: 0.66rem;
  color: #8aa0aa;
}

.hotspot-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: #8aa0aa;
  font-size: 0.9rem;
}

</style>
